import { cn } from "@/lib/utils";
import { formatMoneyCompact, formatNumber } from "@/lib/format";
import type { SupplierPreview } from "@/types/domain";
import { ActionBar, ActionButton, Eyebrow, ScreenTitle } from "../OnboardingShell";
import { AwardCard } from "./AwardCard";

function StatCard({
  label,
  value,
  detail,
  valueClass,
}: {
  label: string;
  value: string;
  detail: string;
  valueClass: string;
}) {
  return (
    <div className="min-w-0 rounded-[18px] border border-slate-200 bg-white p-3.5 sm:p-4">
      <p className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.12em] text-slate-400">
        {label}
      </p>
      <p
        className={cn(
          "mt-1.5 break-words text-[19px] font-extrabold leading-tight tracking-[-0.02em] sm:text-[28px]",
          valueClass,
        )}
      >
        {value}
      </p>
      <p className="text-[12.5px] text-slate-500">{detail}</p>
    </div>
  );
}

/**
 * El primer resultado del proveedor: cuántas obras piden lo que vende y las
 * buenas pro más recientes, con el ganador ya desbloqueado (se las regalamos).
 */
export function ResultStep({
  headline,
  zone,
  preview,
  previewFailed,
  onContinue,
  onBack,
  onSearchAllPeru,
  onChangeProduct,
}: {
  headline: string;
  zone: string;
  preview?: SupplierPreview;
  previewFailed: boolean;
  onContinue: () => void;
  /** Vuelve a la última pregunta (zona). */
  onBack: () => void;
  /** Solo si eligió departamentos: ampliar a todo el Perú es la salida al vacío. */
  onSearchAllPeru?: () => void;
  onChangeProduct: () => void;
}) {
  // La línea ya quedó guardada: que fallen los números no le cierra el paso.
  if (previewFailed || !preview) {
    return (
      <div className="mx-auto w-full max-w-[720px]">
        <Eyebrow tone="emerald">Tu primer resultado</Eyebrow>
        <ScreenTitle>Tu línea quedó guardada.</ScreenTitle>
        <p className="mt-4 max-w-[560px] text-[15px] leading-[1.6] text-slate-600">
          No pudimos cargar las obras contratadas en este momento. Las vas a ver
          en tu panel, y te avisamos cuando una obra que pida {headline} firme
          contrato.
        </p>
        <ActionBar onBack={onBack}>
          <ActionButton tone="emerald" onClick={onContinue} arrow>
            Continuar
          </ActionButton>
        </ActionBar>
      </div>
    );
  }

  const awards = preview.awards ?? [];
  if (preview.works === 0 || awards.length === 0) {
    return (
      <div className="mx-auto w-full max-w-[720px]">
        <Eyebrow tone="emerald">Tu primer resultado</Eyebrow>
        <ScreenTitle>Todavía no hay obras contratadas.</ScreenTitle>
        <p className="mt-4 max-w-[560px] text-[15px] leading-[1.6] text-slate-600">
          En los últimos 90 días ninguna obra contratada pidió {headline} en{" "}
          {zone}. Tu línea ya quedó guardada: te avisamos apenas aparezca una.
        </p>
        <div className="mt-6 flex flex-wrap gap-2">
          {onSearchAllPeru && (
            <button
              type="button"
              onClick={onSearchAllPeru}
              className="cursor-pointer rounded-full border border-emerald-300 bg-white px-5 py-2.5 text-[14px] font-bold text-emerald-700 hover:bg-emerald-50"
            >
              Buscar en todo el Perú
            </button>
          )}
          <button
            type="button"
            onClick={onChangeProduct}
            className="cursor-pointer rounded-full border border-slate-200 bg-white px-5 py-2.5 text-[14px] font-bold text-slate-700 hover:border-slate-300"
          >
            Cambiar producto
          </button>
        </div>
        <ActionBar onBack={onBack}>
          <ActionButton tone="emerald" onClick={onContinue} arrow>
            Continuar
          </ActionButton>
        </ActionBar>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-[720px]">
      <Eyebrow tone="emerald">Tu primer resultado</Eyebrow>
      <ScreenTitle>
        <span className="text-emerald-600">
          {formatNumber(preview.works)} {preview.works === 1 ? "obra" : "obras"}
        </span>{" "}
        {preview.works === 1 ? "pide" : "piden"} {headline}.
      </ScreenTitle>

      {/* Lado a lado también en celular: son dos cifras cortas. */}
      <div className={cn("mt-6 grid gap-3", preview.quantity && "grid-cols-2")}>
        {preview.quantity && (
          <StatCard
            label="Cantidad pedida"
            value={`${formatNumber(Math.round(preview.quantity.amount))} ${preview.quantity.unit}`}
            detail="según expediente técnico"
            valueClass="text-slate-900"
          />
        )}
        <StatCard
          label="Potencial de venta"
          value={formatMoneyCompact(preview.potentialPen)}
          detail="suma de tus partidas"
          valueClass="text-violet-600"
        />
      </div>

      <h2 className="mt-8 text-[19px] font-extrabold tracking-[-0.02em] text-slate-900">
        Obras contratadas
      </h2>

      <div className="mt-3 space-y-3">
        {awards.map((award, index) => (
          <AwardCard
            key={award.contractingId}
            award={award}
            highlight={index === 0}
          />
        ))}
      </div>

      <ActionBar onBack={onBack}>
        <ActionButton tone="emerald" onClick={onContinue} arrow>
          Continuar
        </ActionButton>
      </ActionBar>
    </div>
  );
}
